// What a recurring schedule says it will do, in one sentence.
//
// "Create an invoice for Hartley & Sons for £1,200.00 on the 1st of every
// month, starting 1 June 2026, until cancelled. Due in 30 days. Sent
// automatically."
//
// It lived in `src/js/lib/schedule-format.js`, which was right while the web
// was the only thing that said it. The phone's schedule detail says the same
// thing (#624), and a sentence assembled twice is a sentence that comes to
// describe two different schedules — the ending clause and the delivery
// clause are each three variants deep, which is three chances for one client
// to drift from the other about what somebody's schedule actually does.
//
// **Every formatter it uses was already shared**: the date is the document
// face's own (`formatDocumentDate`, always en-GB) and the money is
// `formatMoney`. Nothing here formats anything itself.
//
// It escapes NOTHING. The web escaped the pieces because it built HTML; this
// answers text, and the caller decides what to do with it. The web's own call
// site escapes the whole sentence now.

import { formatDocumentDate } from "./document-format.js";
import { formatMoney } from "./currencies.js";

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

/** "1st", "2nd", "3rd", "4th" — and "11th" through "13th", which are the trap. */
export function ordinal(n) {
  const suffixes = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]);
}

/**
 * How often, in words, from the frequency and the day the schedule starts on.
 *
 * `startDate` may arrive as a bare "YYYY-MM-DD" or as a full ISO timestamp —
 * the API serialises the Postgres DATE column as a JS Date, which JSON renders
 * as a timestamp. Taking the date portion first is what stops the concat
 * yielding an invalid date, which once surfaced as "the NaNth of every month".
 */
export function scheduleCadenceText(frequency, startDate) {
  if (!startDate) return "…";
  const day = new Date(`${String(startDate).slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(day.getTime())) return "…";
  const dayNumber = day.getUTCDate();
  const dayName = DAY_NAMES[day.getUTCDay()];
  switch (frequency) {
    case "weekly": return `every ${dayName}`;
    case "biweekly": return `every other ${dayName}`;
    case "monthly": return `on the ${ordinal(dayNumber)} of every month`;
    case "quarterly": return `on the ${ordinal(dayNumber)} of every 3 months`;
    case "annually": {
      const month = day.toLocaleString("en-GB", { month: "long", timeZone: "UTC" });
      return `every year on ${month} ${ordinal(dayNumber)}`;
    }
    default: return "…";
  }
}

/** ", for 6 invoices", ", until 1 June 2027", or ", until cancelled". */
export function scheduleEndingText({ maxOccurrences, endDate } = {}) {
  if (maxOccurrences) {
    return `, for ${maxOccurrences} invoice${Number(maxOccurrences) === 1 ? "" : "s"}`;
  }
  if (endDate) return `, until ${formatDocumentDate(endDate)}`;
  return ", until cancelled";
}

/**
 * The whole sentence.
 *
 * @param {object} schedule as the API serves one, with `totalPence`.
 * @returns {string} plain text — no HTML, and nothing escaped.
 */
export function scheduleSentence(schedule = {}) {
  const client = schedule.contactName || "…";
  const totalPence = Number(schedule.totalPence) || 0;
  const amount = totalPence > 0 ? formatMoney(totalPence / 100, schedule.currency) : "…";
  const cadence = scheduleCadenceText(schedule.frequency, schedule.startDate);
  const start = schedule.startDate ? formatDocumentDate(schedule.startDate) : "…";
  const ending = scheduleEndingText(schedule);
  const due = schedule.dueDaysAfter === 0
    ? "Due on receipt."
    : schedule.dueDaysAfter > 0
      ? `Due in ${schedule.dueDaysAfter} days.`
      : "";
  // "Saved as a draft" is not a lesser version of "Sent automatically": it is
  // the difference between a schedule that emails a client by itself and one
  // that leaves an invoice waiting for somebody.
  const delivery = schedule.deliveryMode === "auto_send"
    ? "Sent automatically."
    : "Saved as a draft.";
  // JOINED, not concatenated. A schedule with no payment term left an empty
  // clause between two full stops and the sentence read "…until cancelled.
  // Sent automatically." with two spaces in it — which the web has always
  // done, and which nobody notices until it is next to something else.
  return [
    `Create an invoice for ${client} for ${amount} ${cadence}, starting ${start}${ending}.`,
    due,
    delivery
  ].filter(Boolean).join(" ");
}
